import { useEffect, useState } from 'react';
import moment from 'moment';
import styled, { css } from 'styled-components';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import { FullScreenLoadingCover } from '@prosolve/common-components-ui';
import useDataImplementations from '../dataHooks/useDataImplementations';
import ImplementationCalendarColorControls, { getImplementationColor } from './ImplementationCalendarColorControls';
import ImplementationCalendarEvent from './ImplementationCalendarEvent';
import ImplementationCalendarFilters, { filterImplementations } from './ImplementationCalendarFilters';
import ImplementationCalendarModeFilter, { CALENDAR_DISPLAY_MODES } from './ImplementationCalendarModeFilter';
import ImplementationCalendarToolbar from './ImplementationCalendarToolbar';
import {
  eventsToCalendarEvents,
  getSalesforceEventUrl,
  getSalesforceImplementationUrl,
  implementationsToCalendarEvents,
  normalizeEventCalendarItem,
  splitEventsForVisibleMonth
} from './implementationCalendarUtils';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import useIsMobileView from '../hooks/useIsMobileView';

const localizer = momentLocalizer(moment);

const Page = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  min-height: 100vh;
  background: #f5f5f5;
  color: #2d3640;

  ${({ $view }) => $view === 'TALL' && css`
    height: 180vh;
  `}

  ${({ $view }) => $view === 'TALLEST' && css`
    height: 320vh;
  `}
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 24px 14px;
  border-bottom: solid 1px #d9dee5;
  background: white;

  ${({ $isMobileView }) => $isMobileView && css`
    align-items: flex-start;
    flex-direction: column;
  `}
`

const HeaderText = styled.div`
  flex: 1;
  min-width: 0;
`

const Title = styled.div`
  font-size: 26px;
  font-weight: 700;
  line-height: 1.2;
`

const ControlsSection = styled.div`
  padding: 18px 24px 0;

  ${({ $isMobileView }) => $isMobileView && css`
    padding: 12px 12px 0;
  `}
`

const ControlsPanel = styled.div`
  padding: 12px;
  border: solid 1px #d9dee5;
  border-radius: 8px;
  box-sizing: border-box;
  background: white;
`

const ControlPanelItems = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
`

const CalendarWrapper = styled.div`
  flex: 1;
  min-height: 0;
  padding: 12px 24px 24px;

  ${({ $isMobileView }) => $isMobileView && css`
    padding: 12px;
  `}
`

const CalendarPanel = styled.div`
  position: relative;
  height: calc(100% - 24px);
  min-height: 570px;
  padding: 12px;
  border: solid 1px #d9dee5;
  border-radius: 8px;
  box-sizing: border-box;
  background: white;

  & .rbc-today {
    border: solid 2px #F88700;
    background: hsl(35, 100%, 95%);
  }

  & .rbc-now button {
    font-weight: bold;
  }
`

export default function ImplementationCalendar() {
  const isMobileView = useIsMobileView()
  const {
    isLoading,
    implementationsCalendar,
    eventsCalendar,
    fetchImplementationsCalendar,
    fetchEventsCalendar
  } = useDataImplementations();

  const [date, setDate] = useState(new Date());
  const [view, setView] = useState()
  const [displayMode, setDisplayMode] = useState(CALENDAR_DISPLAY_MODES.IMPLEMENTATIONS)
  const [filters, setFilters] = useState({})
  const [colorFields, setColorFields] = useState({})

  const calendarItems = displayMode === CALENDAR_DISPLAY_MODES.EVENTS
    ? (eventsCalendar || []).map(normalizeEventCalendarItem)
    : (implementationsCalendar || [])
  const hiddenFilterKeys = displayMode === CALENDAR_DISPLAY_MODES.EVENTS
    ? ['product', 'schoolDaySummer']
    : ['trainers'];
  const filteredCalendarItems = filterImplementations(calendarItems, filters)
  const allCalendarEvents = displayMode === CALENDAR_DISPLAY_MODES.EVENTS
    ? eventsToCalendarEvents(filteredCalendarItems)
    : implementationsToCalendarEvents(filteredCalendarItems)

  const {
    monthSpanningEvents,
    calendarEvents
  } = splitEventsForVisibleMonth(allCalendarEvents, date)

  useEffect(() => {
    fetchImplementationsCalendar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (displayMode === CALENDAR_DISPLAY_MODES.EVENTS && !eventsCalendar) {
      fetchEventsCalendar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayMode]);

  const handleNavigate = (functionName, value) => {
    setDate(functionName, value);
  }

  const handleSelectEvent = (calendarEvent) => {
    const recordId = calendarEvent.resource?.id
    const url = displayMode === CALENDAR_DISPLAY_MODES.EVENTS
      ? getSalesforceEventUrl(recordId)
      : getSalesforceImplementationUrl(recordId)
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  const handleDisplayModeChange = (mode) => {
    setDisplayMode(mode);
    setFilters({});
  }

  const eventPropGetter = (event) => {
    const isPast = moment(event.end).isBefore(moment(), 'day');
    const backgroundColor = getImplementationColor(event.resource, colorFields);

    return {
      className: isPast ? 'rcbp-event-past' : 'rcbp-event-upcoming',
      style: backgroundColor ? {
        backgroundColor,
        borderColor: backgroundColor,
        color: 'white'
      } : undefined
    };
  };

  const getEventColor = (event) => getImplementationColor(event.resource, colorFields);

  const Toolbar = (toolbarProps) => (
    <ImplementationCalendarToolbar
      {...toolbarProps}
      view={view}
      monthSpanningEvents={monthSpanningEvents}
      onSelectEvent={handleSelectEvent}
      onViewChange={v => setView(v)}
      getEventColor={getEventColor}
    />
  );

  return (
    <Page $view={view}>
      <Header $isMobileView={isMobileView}>
        <HeaderText>
          <Title>Implementation Calendar</Title>
        </HeaderText>
      </Header>

      <ControlsSection $isMobileView={isMobileView}>
        <ControlsPanel $isMobileView={isMobileView}>
          <ControlPanelItems>
            <ImplementationCalendarModeFilter
              selectedMode={displayMode}
              onChange={handleDisplayModeChange}
            />
            <ImplementationCalendarFilters
              filters={filters}
              implementations={calendarItems}
              hiddenFilterKeys={hiddenFilterKeys}
              isMobileView={isMobileView}
              onChange={setFilters}
            />
            <ImplementationCalendarColorControls
              colorFields={colorFields}
              isMobileView={isMobileView}
              onChange={setColorFields}
            />
          </ControlPanelItems>
        </ControlsPanel>
      </ControlsSection>

      {!isLoading &&
      <CalendarWrapper $isMobileView={isMobileView}>
        <CalendarPanel>
          <Calendar
            key={view}
            localizer={localizer}
            events={calendarEvents}
            eventPropGetter={eventPropGetter}
            date={date}
            onNavigate={handleNavigate}
            defaultView={Views.MONTH}
            views={[Views.MONTH]}
            onSelectEvent={handleSelectEvent}
            components={{
              event: ImplementationCalendarEvent,
              toolbar: Toolbar
            }}
            tooltipAccessor={(event) => event.title}
            popup
            style={{ height: '100%' }}
          />
        </CalendarPanel>
      </CalendarWrapper>
      }

      {isLoading &&
        <FullScreenLoadingCover />
      }
    </Page>
  );
}
